import { useEffect, useState } from 'react';
import type {
  EbayConnectionHealthResponse,
  EbayObservedListing,
  EbayObservedOrder,
  EbayReconciliationItem,
  EbayReconciliationSummary,
  EbayReconciliationView,
  EbaySyncException,
} from '@tcvs/shared/dist/ebay-live';
import { useMarketplaceApi } from './marketplace-api';
type View = 'Reconciliation' | 'Listings' | 'Orders' | 'Exceptions';

const SYNC_POLL_INTERVAL_MS = 1_500;
const SYNC_POLL_ATTEMPTS = 20;
function displayTime(value: string | undefined): string {
  return value === undefined || value === '' ? 'Never' : new Date(value).toLocaleString();
}

function money(value: string | undefined, currency: string | undefined): string {
  if (value === undefined || value === '') return '-';
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency: currency ?? 'USD' }).format(
        numeric,
      )
    : value;
}

function moneyMinor(value: number | undefined, currency: string | undefined): string {
  if (value === undefined) return '-';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency ?? 'USD',
  }).format(value / 100);
}

function reconciliationLabel(view: EbayReconciliationView): string {
  if (view === 'ACTIVE') return 'Active listings';
  if (view === 'LINKED') return 'Linked to TCVS';
  if (view === 'UNLINKED') return 'Unlinked listings';
  if (view === 'INVALID_REFERENCE') return 'Invalid references';
  if (view === 'DUPLICATE_ID') return 'Duplicate TCVS IDs';
  return 'Orphaned sale-ready cards';
}

function reconciliationCategoryLabel(item: EbayReconciliationItem): string {
  if (item.category === 'LINKED') return 'Linked';
  if (item.category === 'UNLINKED') return 'Unlinked';
  if (item.category === 'INVALID_REFERENCE') return 'Invalid reference';
  if (item.category === 'DUPLICATE_ID') return 'Duplicate ID';
  return 'Orphaned';
}

function currentSyncError(
  connectedAt: string | undefined,
  lastFailedAt: string | undefined,
  lastError: string | undefined,
): string | undefined {
  if (lastError === undefined) return undefined;
  if (connectedAt === undefined || lastFailedAt === undefined) return lastError;
  return Date.parse(lastFailedAt) >= Date.parse(connectedAt) ? lastError : undefined;
}

export function EbayOperationsPage(): JSX.Element {
  const marketplace = useMarketplaceApi();
  const [health, setHealth] = useState<EbayConnectionHealthResponse | null>(null);
  const [listings, setListings] = useState<EbayObservedListing[]>([]);
  const [orders, setOrders] = useState<EbayObservedOrder[]>([]);
  const [exceptions, setExceptions] = useState<EbaySyncException[]>([]);
  const [reconciliation, setReconciliation] = useState<EbayReconciliationSummary | null>(null);
  const [reconciliationItems, setReconciliationItems] = useState<EbayReconciliationItem[]>([]);
  const [reconciliationView, setReconciliationView] = useState<EbayReconciliationView>('ACTIVE');
  const [listingStatus, setListingStatus] = useState<'ACTIVE' | 'MISSING' | 'ENDED' | 'SOLD'>(
    'ACTIVE',
  );
  const [listingCursor, setListingCursor] = useState<string | undefined>();
  const [orderCursor, setOrderCursor] = useState<string | undefined>();
  const [exceptionCursor, setExceptionCursor] = useState<string | undefined>();
  const [reconciliationCursor, setReconciliationCursor] = useState<string | undefined>();
  const [view, setView] = useState<View>('Reconciliation');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);
  const inventoryError = currentSyncError(
    health?.connection.connectedAt,
    health?.inventory.lastFailedAt,
    health?.inventory.lastError,
  );
  const orderError = currentSyncError(
    health?.connection.connectedAt,
    health?.orders.lastFailedAt,
    health?.orders.lastError,
  );

  async function refresh(): Promise<void> {
    const [healthResult, listingResult, orderResult, exceptionResult, reconciliationResult] =
      await Promise.all([
        marketplace.getEbayConnectionHealth(),
        marketplace.listEbayObservedListings(listingStatus),
        marketplace.listEbayObservedOrders(),
        marketplace.listEbaySyncExceptions(),
        marketplace.getEbayReconciliation(reconciliationView),
      ]);
    if (!healthResult.ok) {
      setError(healthResult.message ?? healthResult.error);
      return;
    }
    setHealth(healthResult.data);
    setListings(listingResult.ok ? listingResult.data.items : []);
    setListingCursor(listingResult.ok ? listingResult.data.nextCursor : undefined);
    setOrders(orderResult.ok ? orderResult.data.items : []);
    setOrderCursor(orderResult.ok ? orderResult.data.nextCursor : undefined);
    setExceptions(exceptionResult.ok ? exceptionResult.data.items : []);
    setExceptionCursor(exceptionResult.ok ? exceptionResult.data.nextCursor : undefined);
    if (reconciliationResult.ok) {
      setReconciliation(reconciliationResult.data.summary);
      setReconciliationItems(reconciliationResult.data.items);
      setReconciliationCursor(reconciliationResult.data.nextCursor);
    }
    const failed = [listingResult, orderResult, exceptionResult, reconciliationResult].find(
      (result) => !result.ok,
    );
    setError(failed !== undefined && !failed.ok ? (failed.message ?? failed.error) : null);
  }

  useEffect(() => {
    void refresh();
  }, [listingStatus, reconciliationView]);

  async function loadMore(viewToLoad: View): Promise<void> {
    setWorking(`more-${viewToLoad}`);
    if (viewToLoad === 'Reconciliation' && reconciliationCursor !== undefined) {
      const result = await marketplace.getEbayReconciliation(
        reconciliationView,
        reconciliationCursor,
      );
      if (result.ok) {
        setReconciliation(result.data.summary);
        setReconciliationItems((current) => [...current, ...result.data.items]);
        setReconciliationCursor(result.data.nextCursor);
      } else setError(result.message ?? result.error);
    }
    if (viewToLoad === 'Listings' && listingCursor !== undefined) {
      const result = await marketplace.listEbayObservedListings(listingStatus, listingCursor);
      if (result.ok) {
        setListings((current) => [...current, ...result.data.items]);
        setListingCursor(result.data.nextCursor);
      } else setError(result.message ?? result.error);
    }
    if (viewToLoad === 'Orders' && orderCursor !== undefined) {
      const result = await marketplace.listEbayObservedOrders(orderCursor);
      if (result.ok) {
        setOrders((current) => [...current, ...result.data.items]);
        setOrderCursor(result.data.nextCursor);
      } else setError(result.message ?? result.error);
    }
    if (viewToLoad === 'Exceptions' && exceptionCursor !== undefined) {
      const result = await marketplace.listEbaySyncExceptions(exceptionCursor);
      if (result.ok) {
        setExceptions((current) => [...current, ...result.data.items]);
        setExceptionCursor(result.data.nextCursor);
      } else setError(result.message ?? result.error);
    }
    setWorking(null);
  }

  async function connect(): Promise<void> {
    setWorking('connect');
    setError(null);
    const result = await marketplace.startEbayOAuth();
    setWorking(null);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    window.open(result.data.authorizationUrl, '_blank', 'noopener,noreferrer');
  }

  async function synchronize(): Promise<void> {
    const previousInventorySuccess = health?.inventory.lastSucceededAt;
    const previousInventoryFailure = health?.inventory.lastFailedAt;
    const previousOrderSuccess = health?.orders.lastSucceededAt;
    const previousOrderFailure = health?.orders.lastFailedAt;
    setWorking('sync');
    setError(null);
    setNotice('Starting eBay synchronization...');
    const result = await marketplace.requestEbaySync();
    if (!result.ok) {
      setWorking(null);
      setNotice(null);
      setError(result.message ?? result.error);
      return;
    }
    setNotice('Synchronization started. Waiting for eBay results...');
    for (let attempt = 0; attempt < SYNC_POLL_ATTEMPTS; attempt += 1) {
      const healthResult = await marketplace.getEbayConnectionHealth();
      if (!healthResult.ok) {
        setWorking(null);
        setNotice(null);
        setError(healthResult.message ?? healthResult.error);
        return;
      }
      setHealth(healthResult.data);
      const changed =
        healthResult.data.inventory.lastSucceededAt !== previousInventorySuccess ||
        healthResult.data.inventory.lastFailedAt !== previousInventoryFailure ||
        healthResult.data.orders.lastSucceededAt !== previousOrderSuccess ||
        healthResult.data.orders.lastFailedAt !== previousOrderFailure;
      const settled = [healthResult.data.inventory.status, healthResult.data.orders.status].every(
        (status) => status !== 'RUNNING' && status !== 'WAITING',
      );
      if (changed && settled) {
        await refresh();
        setWorking(null);
        setNotice(
          healthResult.data.inventory.status === 'FAILED' ||
            healthResult.data.orders.status === 'FAILED'
            ? 'Synchronization completed with an error. Review the sync status below.'
            : `Synchronization completed. ${healthResult.data.inventory.processedCount ?? healthResult.data.counts.activeListings} active listings retrieved from eBay and ${healthResult.data.counts.orders} orders observed.`,
        );
        return;
      }
      await new Promise<void>((resolve) => window.setTimeout(resolve, SYNC_POLL_INTERVAL_MS));
    }
    setWorking(null);
    setNotice('Synchronization is still running. Use Refresh status to check again.');
  }

  async function disconnect(): Promise<void> {
    if (!window.confirm('Disconnect the eBay seller account from TCVS?')) return;
    setWorking('disconnect');
    setError(null);
    const result = await marketplace.disconnectEbay();
    setWorking(null);
    if (!result.ok) setError(result.message ?? result.error);
    else await refresh();
  }

  return (
    <div className="tcvs-ebay-operations">
      <header className="tcvs-section-heading tcvs-ebay-heading">
        <div>
          <h2>eBay</h2>
          <p>Seller connection and observed marketplace activity.</p>
        </div>
        <span className="tcvs-status-badge">Read only</span>
      </header>

      <section className="tcvs-ebay-health" aria-label="eBay connection health">
        <div>
          <span>Connection</span>
          <strong>{health?.connection.status.replaceAll('_', ' ') ?? 'Checking'}</strong>
          <small>{health?.connection.sellerUsername ?? health?.connection.environment ?? ''}</small>
        </div>
        <div>
          <span>Active listings</span>
          <strong>{health?.counts.activeListings ?? '-'}</strong>
          <small>Last sync {displayTime(health?.inventory.lastSucceededAt)}</small>
        </div>
        <div>
          <span>Orders</span>
          <strong>{health?.counts.orders ?? '-'}</strong>
          <small>Last sync {displayTime(health?.orders.lastSucceededAt)}</small>
        </div>
        <div>
          <span>Exceptions</span>
          <strong>{health?.counts.unresolvedExceptions ?? '-'}</strong>
          <small>Open</small>
        </div>
      </section>

      <div className="tcvs-toolbar tcvs-ebay-actions">
        {health?.connection.status !== 'CONNECTED' && health?.connection.status !== 'DEGRADED' ? (
          <button type="button" onClick={() => void connect()} disabled={working !== null}>
            {working === 'connect' ? 'Connecting...' : 'Connect eBay'}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => void synchronize()}
          disabled={working !== null || health?.connection.refreshTokenConfigured !== true}
        >
          {working === 'sync' ? 'Syncing...' : 'Sync now'}
        </button>
        <button type="button" className="tcvs-linklike" onClick={() => void refresh()}>
          Refresh status
        </button>
        {health?.connection.refreshTokenConfigured === true ? (
          <button
            type="button"
            className="tcvs-linklike"
            onClick={() => void disconnect()}
            disabled={working !== null}
          >
            Disconnect
          </button>
        ) : null}
      </div>

      {health?.connection.status === 'NOT_CONFIGURED' ? (
        <p className="tcvs-warning">eBay seller credentials and RuName are not configured.</p>
      ) : null}
      {health?.connection.refreshTokenConfigured === true && inventoryError !== undefined ? (
        <p className="tcvs-error">Inventory sync: {inventoryError}</p>
      ) : null}
      {health?.connection.refreshTokenConfigured === true && orderError !== undefined ? (
        <p className="tcvs-error">Order sync: {orderError}</p>
      ) : null}
      {notice !== null ? (
        <p className="tcvs-success-text" role="status">
          {notice}
        </p>
      ) : null}
      {error !== null ? <p className="tcvs-error">Error: {error}</p> : null}

      <nav className="tcvs-segmented-control" aria-label="eBay activity views">
        {(['Reconciliation', 'Listings', 'Orders', 'Exceptions'] as const).map((item) => (
          <button
            key={item}
            type="button"
            aria-pressed={view === item}
            onClick={() => setView(item)}
          >
            {item}
          </button>
        ))}
      </nav>

      {view === 'Reconciliation' ? (
        <section className="tcvs-ebay-reconciliation" aria-labelledby="ebay-reconciliation-heading">
          <header className="tcvs-section-heading">
            <div>
              <h3 id="ebay-reconciliation-heading">Marketplace integrity</h3>
              <p>eBay (Main Store) compared with sale-intended TCVS inventory.</p>
            </div>
            <small className="tcvs-muted">
              Evaluated {displayTime(reconciliation?.evaluatedAt)}
            </small>
          </header>
          <div className="tcvs-reconciliation-metrics" aria-label="Marketplace integrity counts">
            <button
              type="button"
              aria-pressed={reconciliationView === 'ACTIVE'}
              onClick={() => setReconciliationView('ACTIVE')}
            >
              <span>Active listings</span>
              <strong>{reconciliation?.activeListings ?? '-'}</strong>
              <small>View all</small>
            </button>
            <button
              type="button"
              aria-pressed={reconciliationView === 'LINKED'}
              onClick={() => setReconciliationView('LINKED')}
            >
              <span>Linked to TCVS</span>
              <strong>
                {reconciliation === null
                  ? '-'
                  : `${reconciliation.linkedListings}/${reconciliation.activeListings}`}
              </strong>
              <small>
                {reconciliation === null || reconciliation.activeListings === 0
                  ? '0%'
                  : `${((reconciliation.linkedListings / reconciliation.activeListings) * 100).toFixed(1)}%`}
              </small>
            </button>
            <button
              type="button"
              aria-pressed={reconciliationView === 'UNLINKED'}
              onClick={() => setReconciliationView('UNLINKED')}
            >
              <span>Unlinked listings</span>
              <strong>{reconciliation?.unlinkedListings ?? '-'}</strong>
              <small>Needs a physical card link</small>
            </button>
            <button
              type="button"
              aria-pressed={reconciliationView === 'DUPLICATE_ID'}
              onClick={() => setReconciliationView('DUPLICATE_ID')}
            >
              <span>Duplicate TCVS IDs</span>
              <strong>{reconciliation?.duplicateCardIds ?? '-'}</strong>
              <small>{reconciliation?.duplicateListings ?? '-'} affected listings</small>
            </button>
            <button
              type="button"
              aria-pressed={reconciliationView === 'INVALID_REFERENCE'}
              onClick={() => setReconciliationView('INVALID_REFERENCE')}
            >
              <span>Invalid references</span>
              <strong>{reconciliation?.invalidReferences ?? '-'}</strong>
              <small>Missing, retired, or conflicting</small>
            </button>
            <button
              type="button"
              aria-pressed={reconciliationView === 'ORPHANED'}
              onClick={() => setReconciliationView('ORPHANED')}
            >
              <span>Orphaned sale-ready</span>
              <strong>{reconciliation?.orphanedCards ?? '-'}</strong>
              <small>No active listing claim</small>
            </button>
            <button type="button" aria-pressed="false" onClick={() => setView('Exceptions')}>
              <span>Needs review</span>
              <strong>{reconciliation?.openExceptions ?? '-'}</strong>
              <small>Open sync exceptions</small>
            </button>
          </div>

          <div className="tcvs-reconciliation-detail-heading">
            <h3>{reconciliationLabel(reconciliationView)}</h3>
          </div>
          <div className="tcvs-table-wrap">
            <table className="tcvs-table tcvs-reconciliation-table">
              <thead>
                {reconciliationView === 'ORPHANED' ? (
                  <tr>
                    <th>TCVS card</th>
                    <th>Title</th>
                    <th>Readiness</th>
                    <th>Expected price</th>
                    <th>Updated</th>
                  </tr>
                ) : (
                  <tr>
                    <th>Marketplace</th>
                    <th>Listing ID</th>
                    <th>Title</th>
                    <th>SKU</th>
                    <th>TCVS card</th>
                    <th>Integrity</th>
                    <th>Live price</th>
                    <th>Last synced</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {reconciliationItems.map((item) =>
                  item.kind === 'ORPHANED_CARD' ? (
                    <tr key={`orphan-${item.cardId}`}>
                      <td>
                        <a
                          href={`/cards/${encodeURIComponent(item.cardId)}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {item.cardId}
                        </a>
                      </td>
                      <td>{item.title ?? '-'}</td>
                      <td>{item.readinessStatus.replaceAll('_', ' ')}</td>
                      <td>{moneyMinor(item.amountMinor, item.currency)}</td>
                      <td>{displayTime(item.updatedAt)}</td>
                    </tr>
                  ) : (
                    <tr key={item.marketplaceListingId}>
                      <td>eBay (Main Store)</td>
                      <td>
                        <a
                          href={`https://www.ebay.com/itm/${encodeURIComponent(item.externalListingId)}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {item.externalListingId}
                        </a>
                      </td>
                      <td>{item.title ?? '-'}</td>
                      <td>{item.customLabel ?? '-'}</td>
                      <td>
                        {item.cardId === undefined ? (
                          (item.referencedCardId ?? 'Unlinked')
                        ) : (
                          <a
                            href={`/cards/${encodeURIComponent(item.cardId)}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {item.cardId}
                          </a>
                        )}
                      </td>
                      <td>
                        {reconciliationCategoryLabel(item)}
                        {item.duplicateListingCount === undefined
                          ? ''
                          : ` (${item.duplicateListingCount})`}
                      </td>
                      <td>{money(item.currentPrice, item.currency)}</td>
                      <td>{displayTime(item.updatedAt)}</td>
                    </tr>
                  ),
                )}
                {reconciliationItems.length === 0 ? (
                  <tr>
                    <td colSpan={reconciliationView === 'ORPHANED' ? 5 : 8} className="tcvs-muted">
                      No {reconciliationLabel(reconciliationView).toLowerCase()}.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          {reconciliationCursor !== undefined ? (
            <button
              type="button"
              onClick={() => void loadMore('Reconciliation')}
              disabled={working !== null}
            >
              Load more
            </button>
          ) : null}
        </section>
      ) : null}

      {view === 'Listings' ? (
        <div>
          <label className="tcvs-field tcvs-ebay-status-filter">
            <span>Listing status</span>
            <select
              value={listingStatus}
              onChange={(event) =>
                setListingStatus(event.target.value as 'ACTIVE' | 'MISSING' | 'ENDED' | 'SOLD')
              }
            >
              <option value="ACTIVE">Active</option>
              <option value="MISSING">Missing from latest report</option>
              <option value="ENDED">Ended</option>
              <option value="SOLD">Sold</option>
            </select>
          </label>
          <div className="tcvs-table-wrap">
            <table className="tcvs-table">
              <thead>
                <tr>
                  <th>Item ID</th>
                  <th>SKU</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Price</th>
                  <th>TCVS card</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((item) => (
                  <tr key={item.marketplaceListingId}>
                    <td>
                      <a
                        href={`https://www.ebay.com/itm/${encodeURIComponent(item.externalListingId)}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {item.externalListingId}
                      </a>
                    </td>
                    <td>{item.customLabel ?? '-'}</td>
                    <td>{item.title ?? '-'}</td>
                    <td>{item.status}</td>
                    <td>{money(item.currentPrice, item.currency)}</td>
                    <td>{item.cardId ?? 'Unlinked'}</td>
                    <td>{displayTime(item.lastSeenAt)}</td>
                  </tr>
                ))}
                {listings.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="tcvs-muted">
                      No synchronized active listings.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          {listingCursor !== undefined ? (
            <button
              type="button"
              onClick={() => void loadMore('Listings')}
              disabled={working !== null}
            >
              Load more
            </button>
          ) : null}
        </div>
      ) : null}

      {view === 'Orders' ? (
        <div>
          <div className="tcvs-table-wrap">
            <table className="tcvs-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Created</th>
                  <th>Payment</th>
                  <th>Fulfillment</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.orderId}>
                    <td>{order.orderId}</td>
                    <td>{displayTime(order.creationDate)}</td>
                    <td>{order.paymentStatus ?? '-'}</td>
                    <td>{order.fulfillmentStatus ?? '-'}</td>
                    <td>{order.lines.length}</td>
                    <td>{money(order.total, order.currency)}</td>
                    <td>{order.hasException ? 'Needs attention' : 'Observed'}</td>
                  </tr>
                ))}
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="tcvs-muted">
                      No synchronized orders.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          {orderCursor !== undefined ? (
            <button
              type="button"
              onClick={() => void loadMore('Orders')}
              disabled={working !== null}
            >
              Load more
            </button>
          ) : null}
        </div>
      ) : null}

      {view === 'Exceptions' ? (
        <div>
          <div className="tcvs-table-wrap">
            <table className="tcvs-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Message</th>
                  <th>Order</th>
                  <th>eBay item</th>
                  <th>Status</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {exceptions.map((item) => (
                  <tr key={item.exceptionId}>
                    <td>{item.exceptionType.replaceAll('_', ' ')}</td>
                    <td>{item.message}</td>
                    <td>{item.orderId ?? '-'}</td>
                    <td>{item.externalListingId ?? '-'}</td>
                    <td>{item.status}</td>
                    <td>{displayTime(item.lastSeenAt)}</td>
                  </tr>
                ))}
                {exceptions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="tcvs-muted">
                      No synchronization exceptions.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          {exceptionCursor !== undefined ? (
            <button
              type="button"
              onClick={() => void loadMore('Exceptions')}
              disabled={working !== null}
            >
              Load more
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
